import startCase from "lodash/startCase";
import React from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useForm, SubmitHandler } from "react-hook-form";
import { toast } from "react-toastify";

import TrashIcon from "./assets/trash.svg?react";
import fetchSingleton from "@/lib/fetchSingleton";
import { humanizeError } from "@/lib/humanizeError";
import { useAuth } from "@/lib/useAuth";
import { useEventSubscriber, useEventPublisher } from "@/lib/useEventEmitter";
import PartLoader from "@/PartLoader";
import PartModal, { type PartModalRef } from "@/PartModal";
import formStyles from "@/styles/form.module.scss";

import styles from "./ModalDelete.module.scss";

const DeleteConfirm: React.FC<{
  entityName: string;
  deleteUrl: string;
  closeModal: () => void;
}> = ({ entityName, deleteUrl, closeModal }) => {
  const {
    formState: { isSubmitting },
    handleSubmit,
  } = useForm();

  const publish = useEventPublisher();

  const { idToken } = useAuth();

  const onSubmit: SubmitHandler<object> = async () => {
    const result = await fetchSingleton(deleteUrl, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    if (result.status === "ERROR") {
      toast.error(humanizeError(result, `Delete ${startCase(entityName)}`));
      return;
    }

    const msg = `${startCase(entityName)} deleted successfully`;
    toast.success(msg, { autoClose: 5000 });

    closeModal();
    publish("deleteEntitySuccess", { name: entityName });
  };

  return (
    <form
      className={formStyles.form}
      onSubmit={handleSubmit(onSubmit, e => {
        console.error(e);
        toast.error(humanizeError(e));
      })}
      onReset={() => {
        closeModal();
      }}
      autoComplete="off"
    >
      <div>{/* something empty for apply margin in formActionButtonsContainer */}</div>
      <div className={formStyles.formActionButtonsContainer}>
        <button
          type="reset"
          className={formStyles.buttonReset}
          data-test-element-type="button"
          data-test-action-type="cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className={formStyles.buttonDelete}
          data-test-element-type="button"
          data-test-action-type="submit"
          data-test-action-is-pending={isSubmitting ? "true" : "false"}
        >
          <PartLoader active={isSubmitting}>Delete</PartLoader>
        </button>
      </div>
    </form>
  );
};

const ModalDelete = React.forwardRef<PartModalRef>((_, ref) => {
  const [deleteProps, setDeleteProps] = React.useState<
    { name: string; title: string; description: string; deleteUrl: string } | undefined
  >();

  const localRef = React.useRef<PartModalRef | null>(null);

  React.useImperativeHandle(ref, () => localRef.current!, []);

  useEventSubscriber("deleteEntityRequest", ({ name, description, deleteUrl }) => {
    setDeleteProps({
      name,
      title: "Delete " + startCase(name),
      description,
      deleteUrl,
    });

    localRef.current?.show();
  });

  return (
    <PartModal ref={localRef} noHeader afterClose={() => setDeleteProps(undefined)}>
      <div className={styles.header}>
        <TrashIcon />
        <span>{deleteProps?.title}</span>
      </div>
      <div className={styles.description}>{deleteProps?.description}</div>
      {deleteProps !== undefined ? (
        <DeleteConfirm
          entityName={deleteProps?.name}
          deleteUrl={deleteProps?.deleteUrl}
          closeModal={() => {
            localRef.current?.hide();
          }}
        />
      ) : null}
    </PartModal>
  );
});

export const useModalDelete = () => {
  const publish = useEventPublisher();

  return React.useCallback(
    (name: string, description: string, deleteUrl: string) => {
      publish("deleteEntityRequest", { name, description, deleteUrl });
    },
    [publish],
  );
};

ModalDelete.displayName = "ModalDelete";

export default ModalDelete;
